import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { apiRequest } from "../../api/http";
import { BranchDetail, BranchListItem, PaginatedResponse, ReviewStatus } from "../../types/admin";

export type BranchListQuery = {
  opened_from?: string;
  opened_to?: string;
  region?: string;
  status?: ReviewStatus | "운영" | "심사대기" | "반려";
  q?: string;
  page?: number;
  page_size?: number;
};

export type UpdateBranchPayload = {
  branchId: number;
  status?: "운영" | "심사대기" | "반려";
  review_status?: ReviewStatus;
};

type BranchesState = {
  list: BranchListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  loadingList: boolean;
  listError: string | null;
  detail: BranchDetail | null;
  loadingDetail: boolean;
  detailError: string | null;
  updating: boolean;
  updateError: string | null;
  deleting: boolean;
  deleteError: string | null;
};

const initialState: BranchesState = {
  list: [],
  totalCount: 0,
  page: 1,
  pageSize: 20,
  loadingList: false,
  listError: null,
  detail: null,
  loadingDetail: false,
  detailError: null,
  updating: false,
  updateError: null,
  deleting: false,
  deleteError: null,
};

const toQuery = (query: BranchListQuery): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

export const fetchBranches = createAsyncThunk<
  PaginatedResponse<BranchListItem>,
  BranchListQuery,
  { rejectValue: string }
>("branches/fetchBranches", async (query, { rejectWithValue }) => {
  try {
    return await apiRequest<PaginatedResponse<BranchListItem>>(`/admin/branches${toQuery(query)}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : "지점 목록 조회에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const fetchBranchDetail = createAsyncThunk<BranchDetail, number, { rejectValue: string }>(
  "branches/fetchBranchDetail",
  async (branchId, { rejectWithValue }) => {
    try {
      return await apiRequest<BranchDetail>(`/admin/branches/${branchId}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "지점 상세 조회에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const updateBranch = createAsyncThunk<BranchDetail, UpdateBranchPayload, { rejectValue: string }>(
  "branches/updateBranch",
  async (payload, { rejectWithValue }) => {
    try {
      const { branchId, ...body } = payload;
      return await apiRequest<BranchDetail>(`/admin/branches/${branchId}`, {
        method: "PATCH",
        body,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "지점 정보 수정에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const deleteBranch = createAsyncThunk<{ deleted: boolean; branch_id: number }, number, { rejectValue: string }>(
  "branches/deleteBranch",
  async (branchId, { rejectWithValue }) => {
    try {
      return await apiRequest<{ deleted: boolean; branch_id: number }>(`/admin/branches/${branchId}`, {
        method: "DELETE",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "지점 삭제에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

const branchesSlice = createSlice({
  name: "branches",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBranches.pending, (state) => {
        state.loadingList = true;
        state.listError = null;
      })
      .addCase(fetchBranches.fulfilled, (state, action) => {
        state.loadingList = false;
        state.list = action.payload.items;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.pageSize = action.payload.page_size;
      })
      .addCase(fetchBranches.rejected, (state, action) => {
        state.loadingList = false;
        state.listError = action.payload ?? "지점 목록 조회에 실패했습니다.";
      })
      .addCase(fetchBranchDetail.pending, (state) => {
        state.loadingDetail = true;
        state.detailError = null;
      })
      .addCase(fetchBranchDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.detail = action.payload;
      })
      .addCase(fetchBranchDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.detailError = action.payload ?? "지점 상세 조회에 실패했습니다.";
      })
      .addCase(updateBranch.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(updateBranch.fulfilled, (state, action) => {
        state.updating = false;
        state.detail = action.payload;
      })
      .addCase(updateBranch.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload ?? "지점 정보 수정에 실패했습니다.";
      })
      .addCase(deleteBranch.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
      })
      .addCase(deleteBranch.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteBranch.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload ?? "지점 삭제에 실패했습니다.";
      });
  },
});

export default branchesSlice.reducer;
